import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { donatePage as d, currencyUi } from '../data';
import { buildIgPayment, calcFeeCoverAmount, newPaymentToken } from '../igIframe';
import { logDonationEvent } from '../api';
import { useAbandonedForm } from '../hooks/useAbandonedForm';
import { useIgFrameHeight } from '../hooks/useIgFrameHeight';
import { useBitRedirect } from '../hooks/useBitRedirect';
import { readUtmParams } from '../utmParams';
import { resolvePurchaseValue, trackPurchase } from '../analytics';
import { FIELD_RULES, validateForm as runValidation } from '../validation';
import ThankYouModal from '../components/ThankYouModal';
import CurrencyPicker from '../components/CurrencyPicker';
import ForeignAddressFields from '../components/ForeignAddressFields';
import { useConsentTermsDialog, TermsLink } from '../components/ConsentTerms';
import {
  DEFAULT_CURRENCY, currencyMeta, formatAmount, presetAmounts,
  needsAddress, countryName,
} from '../currencies';

const LOGO = '/images/logo-purple.png';

// Which donation tab is shown by default when the page loads without a
// ?donation= query param. Change to 'once' to default to the one-time tab or 'monthly' to default to the monthly tab.
const DEFAULT_DONATION_TYPE = 'once';

const emptyForm = {
  firstName: '', lastName: '', city: '', phone: '', email: '',
  // Foreign-currency donations only — see ForeignAddressFields.
  street: '', zipCode: '', countryId: '',
};

// Per-field validation for the donor details, mirroring the join page's rules
// (same patterns + bilingual messages) so the two forms behave identically.
// The shared rules from validation.js, same as the join/campaign/petition pages.
const VALIDATIONS = [
  FIELD_RULES.firstName, FIELD_RULES.lastName, FIELD_RULES.city,
  FIELD_RULES.phone, FIELD_RULES.email,
];

// The address is only required when IsraelGives will require it — i.e. on any
// foreign currency (see needsAddress). Validating it unconditionally would put
// three new mandatory fields in front of every shekel donor.
const FOREIGN_VALIDATIONS = [
  { key: 'street', pattern: /^.{2,}$/, message: currencyUi.errors.street.he },
  { key: 'zipCode', pattern: /^.{2,7}$/, message: currencyUi.errors.zipCode.he },
  { key: 'countryId', pattern: /^\d+$/, message: currencyUi.errors.country.he },
];

function validateForm(form, currency = DEFAULT_CURRENCY) {
  return runValidation(
    form,
    needsAddress(currency) ? [...VALIDATIONS, ...FOREIGN_VALIDATIONS] : VALIDATIONS
  );
}

export default function DonatePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  // /donate?donation=recurring or ?donation=once picks the tab explicitly;
  // otherwise fall back to DEFAULT_DONATION_TYPE above.
  const donationParam = searchParams.get('donation');
  const initialRecurring = donationParam ? donationParam === 'recurring' : DEFAULT_DONATION_TYPE === 'monthly';

  const [recurring, setRecurring] = useState(initialRecurring);
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [amount, setAmount] = useState('');
  const [custom, setCustom] = useState(''); // free "other" amount
  const [coverFee, setCoverFee] = useState(true); // "cover the transaction fee" checkbox, checked by default
  const [showForm, setShowForm] = useState(false); // amount widget replaced by the inline donate form
  const [step, setStep] = useState('amount'); // amount | payment | success
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState('idle'); // idle | submitting | error
  const [errorMsg, setErrorMsg] = useState('');
  const [errors, setErrors] = useState({}); // per-field validation errors
  const [igSubmitted, setIgSubmitted] = useState(false); // donor info POSTed -> show hosted card iframe
  const [showThankYou, setShowThankYou] = useState(false); // share dialog, opened on a completed donation
  const [openTerms, termsDialog] = useConsentTermsDialog();
  // Latest donor snapshot for the mount-time ig-donation-complete handler, whose
  // closure would otherwise capture the empty initial form/amount.
  const latestDonor = useRef({});
  latestDonor.current = { form, amount, recurring, currency };
  // Guards against the ig-donation-complete message (and its tracking calls)
  // being processed twice for the same donation, e.g. if the iframe delivers
  // it more than once.
  const completedDonationRef = useRef(false);
  // Per-attempt id linking the 'initiated' donor details to the completion
  // that comes back through IsraelGives' successurl. Set in onSubmit.
  const paymentTokenRef = useRef('');

  // Once completed, stays suppressed even if the user clicks "back" to the amount
  // step afterward (step no longer 'success') while the donor fields are still
  // populated — otherwise closing the tab re-triggers an abandoned-form send for
  // an already-successful donation.
  useAbandonedForm({ form, page: 'תרומה', donationType: recurring ? 'חודשי' : 'חד פעמי', amount, submitted: completedDonationRef.current, activity: igSubmitted });

  // Lets the hosted payment frame grow to fit its content (see the hook).
  const igFrameRef = useIgFrameHeight();
  useBitRedirect(); // mobile bit: their framed page hands us the link to open

  // Always land at the top on load/refresh, ignoring the browser's restored scroll position.
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
  }, []);

  // A donor who paid through a method that LEFT the iframe (PayPal) never got
  // the in-page success flow — ig-complete.html sends them back here with
  // ?donated=1 instead. Their donation is already recorded server-side by then;
  // this is purely the confirmation they'd otherwise never see.
  useEffect(() => {
    if (searchParams.get('donated') !== '1') return;
    completedDonationRef.current = true; // never chase them as an abandoned form
    setShowThankYou(true);
    const next = new URLSearchParams(searchParams);
    next.delete('donated'); // so a refresh doesn't reopen the dialog
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // The IsraelGives frame redirects to /ig-complete.html (same-origin) on a
  // successful charge, which postMessages us. Swap to our own success screen.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data && e.data.type === 'ig-donation-complete') {
        // Answer first, before any early return: ig-complete.html takes the
        // whole tab back to the campaign if nothing acknowledges it, and a
        // duplicate message is still proof that this page is handling it.
        if (e.source) e.source.postMessage({ type: 'ig-donation-ack' }, window.location.origin);
        if (completedDonationRef.current) return;
        completedDonationRef.current = true;
        // The completion is reported to the backend by ig-complete.html, not
        // here — that's the only path that also survives a payment method
        // which left the iframe (PayPal). This handler owns the UI and the
        // conversions; duplicating the report would just be a second write.
        // The currency the donor chose, not a hardcoded ILS — reporting a $50
        // donation as ₪50 would misprice every conversion in Ads and GA4. Read
        // off the ref because this handler is mounted once and its closure
        // would otherwise hold the initial currency.
        const paidCurrency = latestDonor.current.currency || DEFAULT_CURRENCY;
        const paidValue = resolvePurchaseValue(latestDonor.current.total, e.data.sum);
        if (window.fbq) window.fbq('track', 'Purchase', { value: paidValue, currency: paidCurrency });
        if (window.gtag) {
          window.gtag('event', 'conversion', { send_to: 'AW-718777808/kPpUCPHXhu0bENDb3tYC', transaction_id: e.data.donationId || '' });
        }
        // GA4 + the other GTM purchase tags — see analytics.js for why this isn't a gtag() call.
        trackPurchase({ transactionId: e.data.donationId, value: paidValue, currency: paidCurrency });
        setIgSubmitted(false);
        setStatus('idle');
        setStep('success');
        setShowThankYou(true); // open the share dialog over the success screen
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Once donor details are submitted, POST the hidden form into the frame so
  // IsraelGives shows the hosted card fields. The desktop and mobile hero
  // layouts are BOTH always in the DOM (CSS hides one), so two hosted forms +
  // iframes exist at once. Each now targets a uniquely-named iframe; we submit
  // only the form inside the currently-visible layout, so the POST can never
  // load into a hidden duplicate frame and leave the visible one blank (which
  // is exactly what broke the iframe on mobile while working on desktop).
  useEffect(() => {
    if (!igSubmitted) return;
    document.querySelectorAll('form.mf-hosted-post').forEach((form) => {
      const layout = form.closest('.donate-desktop, .donate-editorial, .member-main') || form;
      if (layout.getClientRects().length > 0) form.submit();
    });
  }, [igSubmitted]);

  // Shekel donors keep the ladder from data.js; foreign currencies get round
  // numbers of their own rather than converted shekel amounts (currencies.js).
  const amounts = presetAmounts(currency, recurring, recurring ? d.monthlyAmounts : d.onceAmounts);
  const cur = currencyMeta(currency);
  // The "other amount" input: a value below this is invalid (red border) and
  // blocks moving on to the donor form, matching the join page's minimum.
  const minAmount = cur.min;
  // Fee is computed off the chosen amount but only added to the sum sent to
  // IsraelGives when the donor opts in — the displayed donation amount stays
  // as chosen. Denominated in the donation's own currency.
  const feeAmount = calcFeeCoverAmount(amount, currency);
  const totalAmount = coverFee ? Math.round((Number(amount) + feeAmount) * 100) / 100 : Number(amount);
  latestDonor.current.total = totalAmount;
  const setField = (key) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    // Donor fields stay editable even while the hosted iframe is open. Editing
    // one invalidates the already-POSTed iframe (it holds the old values), so we
    // drop back to the form; re-submitting reloads the iframe with the corrected
    // details and avoids charging data that differs from what's shown.
    if (igSubmitted) setIgSubmitted(false);
  };

  // The custom "other" amount field: below the currency's minimum is invalid
  // (red border), and blocks the "next" button below.
  const customNum = Number(custom);
  const customInvalid = custom.trim() !== '' && (!Number.isFinite(customNum) || customNum < minAmount);
  const customValid = custom.trim() !== '' && !customInvalid;

  const selectType = (isMonthly) => {
    setRecurring(isMonthly);
    // The currency survives the switch: every currency is offered on both
    // tabs now (see currencies.js), so there is nothing to fall back from.
    // Only the amount is cleared — the monthly ladder is a different one.
    setAmount('');
    setCustom('');
  };

  // Presets differ per currency, so a stale selection would show the wrong
  // ladder highlighted (or an amount that isn't on the new one at all).
  const selectCurrency = (code) => {
    setCurrency(code);
    setAmount('');
    setCustom('');
  };
  // "הבא": carry the chosen (or custom) amount into the inline donate form
  // (shown in place of the amount widget, in the hero's left column).
  const goToPayment = () => {
    // Always re-enter the donor form fresh: if a prior attempt left igSubmitted
    // true (e.g. user went back and switched tabs), leaving it set would render
    // a new, un-POSTed hosted iframe straight away -> blank frame.
    setIgSubmitted(false);
    setErrors({}); // drop any stale validation errors from a prior visit
    if (custom.trim() !== '') {
      if (customInvalid) return;
      setAmount(custom.trim());
      setShowForm(true);
      return;
    }
    if (!amount || Number(amount) <= 0) return;
    setShowForm(true);
  };

  // The amount-selection widget shown in place of the top type buttons (see img.png).
  const renderAmountWidget = () => (
    <>
      {/* Home-link logo above the title (desktop only, nudged 200px right). */}
      <a
        className="donate-home-logo"
        href="https://www.standing-together.org/"
        aria-label="עומדים ביחד نقف معًا"
      >
        <img src={LOGO} alt="עומדים ביחד نقف معًا" />
      </a>
      <h2 className="donate-page-title donate-page-title--outside">
        <span className="bi-he">{d.title.he}</span>
        <span className="bi-ar">{d.title.ar}</span>
      </h2>
      <div className="donate-widget donate-widget--inline">
        <div className="donate-toggle">
        <button type="button" className={recurring ? 'active' : ''} onClick={() => selectType(true)}>
          <span className="bi-he">{d.types.monthly.he}</span>{' '}
          <span className="bi-ar">{d.types.monthly.ar}</span>
        </button>
        <button type="button" className={!recurring ? 'active' : ''} onClick={() => selectType(false)}>
          <span className="bi-he">{d.types.once.he}</span>{' '}
          <span className="bi-ar">{d.types.once.ar}</span>
        </button>
      </div>
      <CurrencyPicker
        id="donate-currency"
        value={currency}
        onChange={selectCurrency}
      />
      <div className="amount-grid">
        {amounts.map((a) => (
          <button
            type="button"
            key={a}
            className={`amount ${!custom && amount === a ? 'active' : ''}`}
            onClick={() => { setAmount(a); setCustom(''); }}
          >
            <span className="amount-value">{formatAmount(a, currency)}</span>
            {recurring ? (
              <span className="per-month">
                <span className="bi-he">{d.perMonth.he}</span>{' '}
                <span className="bi-ar">{d.perMonth.ar}</span>
              </span>
            ) : null}
          </button>
        ))}
        <input
          className={`amount donate-other ${custom.trim() !== '' ? 'active' : ''} ${customInvalid ? 'donate-other--invalid' : ''}`}
          type="number"
          inputMode="numeric"
          min={minAmount}
          placeholder={`${d.other.he} (${cur.symbol})`}
          value={custom}
          onChange={(e) => { setCustom(e.target.value); setAmount(''); }}
        />
      </div>
      <button
        type="button"
        className="donate-next"
        onClick={goToPayment}
        disabled={!amount && !customValid}
      >
        <span className="bi-he">{d.next.he}</span>{' '}
        <span className="bi-ar">{d.next.ar}</span>
      </button>
      </div>
    </>
  );

  // Hidden POST form + iframe: IsraelGives renders the hosted card fields and
  // its own "pay" button inside the frame. Placeholders in {curly braces} are
  // substituted by IsraelGives and handed back to /ig-complete.html as query
  // params. `successurl` must be the LAST param and URL-encoded.
  const renderHostedPayment = (frameName) => {
    const { action, fields } = buildIgPayment({
      // `street` is our label for what IsraelGives calls `address`.
      profile: { ...form, address: form.street },
      amount: totalAmount,
      currency,
      frequency: recurring ? '2' : '1', // 1 = one-time, 2 = recurring monthly
      token: paymentTokenRef.current,
      returnPath: window.location.pathname, // where to land a donor who came back off-frame
    });
    return (
      <div className="mf-hosted">
        <form method="POST" action={action} target={frameName} className="mf-hosted-post">
          {Object.entries(fields).map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
        </form>
        <iframe ref={igFrameRef} className="mf-iframe" title="IsraelGives" name={frameName} />
        <p className="member-consent">
          <span className="bi-he">{d.consent.he}<TermsLink onOpen={openTerms}>{d.consent.heTerms}</TermsLink></span>
          <span className="bi-ar">{d.consent.ar}<TermsLink onOpen={openTerms}>{d.consent.arTerms}</TermsLink></span>
        </p>
        <button type="button" className="mf-hosted-edit" onClick={() => setIgSubmitted(false)}>
          <span className="bi-he">{d.back.he}</span>{' '}
          <span className="bi-ar">{d.back.ar}</span>
        </button>
      </div>
    );
  };

  // Shared donor fields (used by the inline hero form and the member-screen
  // checkout). The wrapping <form> and any amount selection are supplied by the
  // caller. Card details are collected by the IsraelGives hosted iframe below,
  // not here.
  const renderDonorFields = (frameName) => (
    <>
      <div className="mf-row">
        <label className="mf-field">
          <span className="mf-label">{d.fields.firstName.he} {d.fields.firstName.ar} <em>*</em></span>
          <input autoComplete="given-name" className={errors.firstName ? 'mf-invalid' : undefined} value={form.firstName} onChange={setField('firstName')} />
          {errors.firstName && <span className="mf-error">{errors.firstName}</span>}
        </label>
        <label className="mf-field">
          <span className="mf-label">{d.fields.lastName.he} {d.fields.lastName.ar} <em>*</em></span>
          <input autoComplete="family-name" className={errors.lastName ? 'mf-invalid' : undefined} value={form.lastName} onChange={setField('lastName')} />
          {errors.lastName && <span className="mf-error">{errors.lastName}</span>}
        </label>
      </div>
      <div className="mf-row">
        <label className="mf-field">
          <span className="mf-label">{d.fields.city.he} {d.fields.city.ar} <em>*</em></span>
          <input autoComplete="address-level2" className={errors.city ? 'mf-invalid' : undefined} value={form.city} onChange={setField('city')} />
          {errors.city && <span className="mf-error">{errors.city}</span>}
        </label>
        <label className="mf-field">
          <span className="mf-label">{d.fields.phone.he} {d.fields.phone.ar} <em>*</em></span>
          <input type="tel" autoComplete="tel" className={errors.phone ? 'mf-invalid' : undefined} value={form.phone} onChange={setField('phone')} />
          {errors.phone && <span className="mf-error">{errors.phone}</span>}
        </label>
      </div>
      <label className="mf-field">
        <span className="mf-label">{d.fields.email.he} {d.fields.email.ar} <em>*</em></span>
        <input type="email" autoComplete="email" className={errors.email ? 'mf-invalid' : undefined} value={form.email} onChange={setField('email')} />
        {errors.email && <span className="mf-error">{errors.email}</span>}
      </label>

      {needsAddress(currency) && (
        <ForeignAddressFields form={form} errors={errors} setField={setField} />
      )}

      <div className="mf-divider" aria-hidden="true">
        <span className="mf-divider-dot" />
      </div>

      {!igSubmitted ? (
        <>
          {status === 'error' && <p className="join-error member-error">{errorMsg}</p>}
          <div className="member-secure">
            <img className="member-secure-badge" src="/images/pci-dss.webp" alt="PCI DSS Compliant" width="130" loading="lazy" />
            <p className="member-secure-text">
              <span className="bi-he">{d.secureNotice.he}</span>
              <span className="bi-ar">{d.secureNotice.ar}</span>
            </p>
          </div>
          <label className="mf-check mf-cover-fee">
            <input
              type="checkbox"
              checked={coverFee}
              onChange={(e) => setCoverFee(e.target.checked)}
            />
            <span>
              <span className="bi-he">{d.coverFee.he.replace('{amount}', formatAmount(feeAmount, currency))}</span>
              <span className="bi-ar">{d.coverFee.ar.replace('{amount}', formatAmount(feeAmount, currency))}</span>
            </span>
          </label>
          <button className="member-submit" type="submit" disabled={!amount}>
            {`${d.pay.he} ${d.pay.ar}`}
          </button>
          <p className="member-consent">
            <span className="bi-he">{d.consent.he}<TermsLink onOpen={openTerms}>{d.consent.heTerms}</TermsLink></span>
            <span className="bi-ar">{d.consent.ar}<TermsLink onOpen={openTerms}>{d.consent.arTerms}</TermsLink></span>
          </p>
        </>
      ) : (
        renderHostedPayment(frameName)
      )}
    </>
  );

  // The donate form shown in place of the amount widget (hero left column)
  // once an amount has been chosen and "הבא" pressed. Header matches img_1:
  // back link (top), centered bilingual title, then the payment summary line.
  const renderDonateForm = (frameName) => (
    <form className="member-form donate-inline-form" onSubmit={onSubmit} noValidate>
      <button type="button" className="donate-inline-back" onClick={() => setShowForm(false)}>
        <span aria-hidden="true">»</span>
        <span className="donate-inline-back-text">
          <span className="bi-he">{d.back.he}</span>
          <span className="bi-ar">{d.back.ar}</span>
        </span>
      </button>
      <div className="donate-inline-header">
        <h2 className="donate-inline-title">
          <span className="bi-he">{d.title.he}</span>
          <span className="bi-ar">{d.title.ar}</span>
        </h2>
        <div className="donate-inline-amount">
          {recurring ? 'תשלום חודשי בסך' : d.paymentOf.he} {formatAmount(amount, currency)}
        </div>
      </div>
      {renderDonorFields(frameName)}
    </form>
  );

  // "Donate now" CTAs in the 2nd/3rd sections: smoothly glide to the top and open
  // the hero amount picker on the one-time tab (as if the user clicked that tab).
  const donateOnce = () => {
    setRecurring(false);
    setAmount('');
    setCustom('');
    setShowForm(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Submitting the donor form no longer charges a card here — it hands the
  // donor + amount to the IsraelGives hosted iframe (see renderHostedPayment),
  // which collects the card and processes the payment. Donor fields are
  // validated here (same rules as the join page) before we hand off.
  const onSubmit = (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) return;
    const found = validateForm(form, currency);
    setErrors(found);
    if (Object.keys(found).length) return; // block hand-off on validation errors
    setErrorMsg('');
    // One token per attempt, generated here and reused by the hidden POST form
    // below — it's what lets the backend match this donor to the completion.
    paymentTokenRef.current = newPaymentToken();
    logDonationEvent({
      stage: 'initiated', page: 'תרומה', campaignName: 'donate',
      token: paymentTokenRef.current,
      firstName: form.firstName, lastName: form.lastName, email: form.email,
      phone: form.phone, city: form.city, amount, recurring, coverFee,
      frequency: recurring ? '2' : '1',
      // The completion that comes back through IsraelGives' successurl carries
      // none of this, so it rides in here and is rejoined server-side. Without
      // `currency` a $50 donation would be filed in Gramsci as ₪50.
      currency,
      street: form.street, zipCode: form.zipCode, country: countryName(form.countryId),
      ...readUtmParams(),
    });
    setIgSubmitted(true);
  };

  // Back-to-amount from the success screen: only reachable post-donation (the
  // 'payment' step below is otherwise unused), so it's safe to always drop the
  // donor fields — a second donation shouldn't silently reuse stale info.
  const backToAmountFromSuccess = () => {
    setForm(emptyForm);
    setStep('amount');
  };

  // ---- Checkout + success share the member-screen layout (mirrors the join page) ----
  if (step === 'payment' || step === 'success') {
    return (
      <div className="member-screen">
        <ThankYouModal open={showThankYou} onClose={() => setShowThankYou(false)} />
        {termsDialog}

        <button type="button" className="member-back" onClick={backToAmountFromSuccess}>
          <span aria-hidden="true">→ </span>
          <span className="bi-he">{d.back.he}</span>
          <span className="bi-ar">{d.back.ar}</span>
        </button>

        <div className="member-grid">
          <div className="member-main">
            <a className="member-logo" href="/donate">
              <img src={LOGO} alt="עומדים ביחד نقف معًا" />
            </a>
            <h1 className="member-heading">
              <span className="bi-he">{d.title.he}</span>
              <span className="bi-ar">{d.title.ar}</span>
            </h1>

            {step === 'payment' ? (
              <form className="member-form" onSubmit={onSubmit} noValidate>
                {/* amount selection — shown when arriving via a mobile CTA without a chosen amount */}
                {!amount && (
                  <div className="mf-amounts">
                    <div className="donate-toggle donate-toggle--form">
                      <button type="button" className={!recurring ? 'active' : ''} onClick={() => { setRecurring(false); setAmount(''); }}>
                        <span className="bi-he">{d.types.once.he}</span>
                        <span className="bi-ar">{d.types.once.ar}</span>
                      </button>
                      <button type="button" className={recurring ? 'active' : ''} onClick={() => { setRecurring(true); setAmount(''); }}>
                        <span className="bi-he">{d.types.monthly.he}</span>
                        <span className="bi-ar">{d.types.monthly.ar}</span>
                      </button>
                    </div>
                    <div className="mf-amount-grid">
                      {amounts.map((a) => (
                        <button
                          type="button"
                          key={a}
                          className={`mf-amount ${amount === a ? 'active' : ''}`}
                          onClick={() => setAmount(a)}
                        >
                          {formatAmount(a, currency)}{recurring ? <span className="per-month"> {d.perMonth.he}</span> : null}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {renderDonorFields('ig-frame-member')}
              </form>
            ) : (
              <div className="member-success">
                <div className="success-check" aria-hidden="true">✓</div>
                <h2>
                  <span className="bi-he">{d.thanks.he}</span>
                  <span className="bi-ar">{d.thanks.ar}</span>
                </h2>
                <p>
                  <span className="bi-he">{d.thanksBody.he}</span>
                  <span className="bi-ar">{d.thanksBody.ar}</span>
                </p>
              </div>
            )}
          </div>

          {/* selected-amount summary card */}
          <aside className="member-summary">
            <div className="member-summary-card">
              <p className="summary-label">
                <span className="bi-he">{d.paymentOf.he}</span>
              </p>
              <div className="summary-value">{formatAmount(amount, currency)}{recurring ? ` / ${d.perMonth.he}` : ''}</div>
              <button type="button" className="summary-change" onClick={backToAmountFromSuccess}>
                {d.back.he}
              </button>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  // ---- Amount step: desktop = panel + editorial column; mobile (≤768) = editorial scroll ----
  const aside = d.aside;
  return (
    <div className="join donate-route">
      {termsDialog}
      {/* ---- Desktop (>768px) layout: matches the 3 desktop reference screens ---- */}
      <div className="donate-desktop">
        {/* Section 1 — buttons (left) + lead text (right) */}
        <section className="dd-hero dd-hero--picking">
          <div className="dd-hero-buttons">
            {showForm ? renderDonateForm('ig-frame-desktop') : renderAmountWidget()}
          </div>
          <div className="dd-lead">
            <p className="dd-lead-line bi-he">
              {aside.paragraphs[0].he.split(d.lead1.hePurple)[0]}
              <span className="de-em">{d.lead1.hePurple}</span>
            </p>
            <p className="dd-lead-line bi-ar">
              {aside.paragraphs[0].ar.split(d.lead1.arPurple)[0]}
              <span className="de-em">{d.lead1.arPurple}</span>
            </p>
          </div>
        </section>

        {/* Section 2 — ¶2 text + CTA (left) + photo (right) */}
        <section className="dd-mid">
          <div className="dd-mid-text">
            <p className="dd-m2-head bi-he">{d.mission2.headHe}</p>
            <p className="dd-m2-body bi-he">{d.mission2.bodyHe}</p>
            <p className="dd-m2-head bi-ar">{d.mission2.headAr}</p>
            <p className="dd-m2-body bi-ar">{d.mission2.bodyAr}</p>
            <button className="dd-cta" onClick={donateOnce}>
              <span className="bi-he">{d.donateNow.he}</span>
              <span className="bi-ar">{d.donateNow.ar}</span>
            </button>
          </div>
          <img className="dd-photo" loading="lazy" src="/images/donate.png" alt="" />
        </section>

        {/* Section 3 — manifesto + bank/Bit + CTAs */}
        <section className="dd-footer">
          <p className="dd-para">
            <span className="bi-he">
              {aside.paragraphs[2].he.split('הצטרפו אלינו').flatMap((part, i) =>
                i === 0 ? [part] : [<span className="de-em" key={i}>הצטרפו אלינו</span>, part]
              )}
            </span>
            <span className="bi-ar dd-para-ar">
              {aside.paragraphs[2].ar.split('إنضمّوا إلينا').flatMap((part, i) =>
                i === 0 ? [part] : [<span className="de-em" key={i}>إنضمّوا إلينا</span>, part]
              )}
            </span>
          </p>

          <div className="dd-bank">
            {/* Bit (left) */}
            <div className="dd-bank-col">
              <p className="dd-bank-title bi-he">{aside.bitTitle.he}</p>
              <p className="dd-bank-title bi-ar">{aside.bitTitle.ar}</p>
              <p className="dd-bank-line bi-he">{aside.bitLine.he}</p>
              <p className="dd-bank-line bi-ar">{aside.bitLine.ar}</p>
            </div>
            {/* intro + account (right) */}
            <div className="dd-bank-col">
              <p className="dd-bank-title bi-he">{aside.bankTitle.he}</p>
              <p className="dd-bank-title bi-ar">{aside.bankTitle.ar}</p>
              <div className="dd-bank-account">
                {aside.bankLines.map((l, i) => (
                  <p key={i} className="dd-bank-line bi-he">{l.he}</p>
                ))}
                {aside.bankLines.map((l, i) => (
                  l.ar ? <p key={`ar-${i}`} className="dd-bank-line bi-ar">{l.ar}</p> : null
                ))}
              </div>
            </div>
          </div>

          <div className="dd-actions">
            <button className="dd-cta" onClick={donateOnce}>
              <span className="bi-he">{d.donateNow.he}</span>
              <span className="bi-ar">{d.donateNow.ar}</span>
            </button>
            <a
              className="dd-who"
              href="https://www.standing-together.org/about"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="bi-he">{d.whoWeAre.he}</span>
              <span className="bi-ar">{d.whoWeAre.ar}</span>
            </a>
          </div>
        </section>
      </div>

      {/* ---- Mobile (≤768px) editorial scroll: matches the 4 reference screens ---- */}
      <div className="donate-editorial">
        {/* Home-link logo, centered at the very top (mobile only). */}
        <a
          className="donate-editorial-logo"
          href="https://www.standing-together.org/"
          aria-label="עומדים ביחד نقف معًا"
        >
          <img src={LOGO} alt="עומדים ביחד نقف معًا" />
        </a>
        {/* Screen 1 — mission ¶1 + type buttons (grouped hero block) + photo */}
        <div className="de-hero">
          <div className="de-lead">
            <p className="de-lead-line bi-he">
              {d.lead1.he}<span className="de-em">{d.lead1.hePurple}</span>
            </p>
            <p className="de-lead-line de-lead-line--ar bi-ar">
              {d.lead1.ar}<span className="de-em">{d.lead1.arPurple}</span>
            </p>
          </div>

          <div className="de-types">
            {showForm ? renderDonateForm('ig-frame-mobile') : renderAmountWidget()}
          </div>
        </div>

        {/* Screen 2 — image → mission ¶2 → CTA, all on a white section */}
        <div className="de-mid">
          <img className="de-photo" loading="lazy" src="/images/donate.png" alt="" />

          <div className="de-m2">
            <p className="de-m2-head bi-he">{d.mission2.headHe}</p>
            <p className="de-m2-body bi-he">{d.mission2.bodyHe}</p>
            <p className="de-m2-head de-m2-head--ar bi-ar">{d.mission2.headAr}</p>
            <p className="de-m2-body de-m2-body--ar bi-ar">{d.mission2.bodyAr}</p>
          </div>
          <button className="de-cta" onClick={donateOnce}>
            <span className="bi-he">{d.donateNow.he}</span>
            <span className="bi-ar">{d.donateNow.ar}</span>
          </button>
        </div>

        {/* Screens 3 & 4 sit on a light lavender footer block */}
        <div className="de-footer">
          {/* Screen 3 — manifesto ¶3 */}
          <p className="de-para">
            <span className="bi-he">
              {aside.paragraphs[2].he.split('הצטרפו אלינו').flatMap((part, i) =>
                i === 0 ? [part] : [<span className="de-em" key={i}>הצטרפו אלינו</span>, part]
              )}
            </span>
            <span className="bi-ar de-para-ar">
              {aside.paragraphs[2].ar.split('إنضمّوا إلينا').flatMap((part, i) =>
                i === 0 ? [part] : [<span className="de-em" key={i}>إنضمّوا إلينا</span>, part]
              )}
            </span>
          </p>

          {/* Screen 4 — bank transfer + Bit details, in two columns (He | Ar) */}
          <div className="de-bank">
            {/* intro: He then Ar */}
            <div className="de-bank-group">
              <p className="de-bank-title bi-he">{aside.bankTitle.he}</p>
              <p className="de-bank-title bi-ar">{aside.bankTitle.ar}</p>
            </div>
            {/* Bit: He then Ar */}
            <div className="de-bank-group">
              <p className="de-bank-title bi-he">{aside.bitTitle.he}</p>
              <p className="de-bank-title bi-ar">{aside.bitTitle.ar}</p>
              <p className="de-bank-line bi-he">{aside.bitLine.he}</p>
              <p className="de-bank-line bi-ar">{aside.bitLine.ar}</p>
            </div>
            {/* account details: He lines then Ar lines */}
            <div className="de-bank-group">
              {aside.bankLines.map((l, i) => (
                <p key={i} className="de-bank-line bi-he">{l.he}</p>
              ))}
              {aside.bankLines.map((l, i) => (
                l.ar ? <p key={`ar-${i}`} className="de-bank-line bi-ar">{l.ar}</p> : null
              ))}
            </div>
          </div>

          <button className="de-cta" onClick={donateOnce}>
            <span className="bi-he">{d.donateNow.he}</span>
            <span className="bi-ar">{d.donateNow.ar}</span>
          </button>

          <a
            className="de-who"
            href="https://www.standing-together.org/about"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="bi-he">{d.whoWeAre.he}</span>
            <span className="bi-ar">{d.whoWeAre.ar}</span>
          </a>
        </div>
      </div>
    </div>
  );
}
